import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyRegistrationForm } from '../../Components/forms/CompanyRegistrationForm';
import { PlanSelection } from '../../Components/forms/PlanSelection';
import { companyApi } from '../../services/api';
import Navbar from '../../Components/Navbar';
import { useTranslation } from '../../../TranslationContext';

type RegistrationStep = 'company-info' | 'plan-selection' | 'confirmation';

export const BecomeClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('company-info');
  const [registrationData, setRegistrationData] = useState<any>({});
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [showDemoModal, setShowDemoModal] = useState<boolean>(true);
  const [playingDemo, setPlayingDemo] = useState<boolean>(false);

  // Réinitialiser le modal quand le composant est monté
  useEffect(() => {
    setShowDemoModal(true);
    setPlayingDemo(false);
  }, []);

  const handleCompanySubmit = async (data: any) => {
    setRegistrationData({ ...registrationData, ...data });
    setCurrentStep('plan-selection');
  };

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
  };

  const handleSubmit = async () => {
    try {
      const finalData = {
        ...registrationData,
        plan: selectedPlan,
      };
      await companyApi.register(finalData);
      setCurrentStep('confirmation');
    } catch (error) {
      console.error(t('error_registering_company'), error);
    }
  };

  const handleWatchDemo = () => {
    setPlayingDemo(true);
    setShowDemoModal(false);
  };

  const handleSkipDemo = () => {
    setShowDemoModal(false);
  };

  const handleCloseDemoVideo = () => {
    setPlayingDemo(false);
  };

  return (
    <div>
      <Navbar />
      <div className="flex-1 bg-gray-50 min-h-screen">
        {/* Modal de démonstration avec arrière-plan transparent */}
        {showDemoModal && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t('demo_modal_title')}</h3>
              <p className="text-gray-600 mb-6">{t('demo_modal_description')}</p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleSkipDemo}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('demo_modal_skip')}
                </button>
                <button
                  onClick={handleWatchDemo}
                  className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  {t('demo_modal_watch')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vidéo de démonstration en mode ajustable */}
        {playingDemo && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-[60%] max-w-4xl relative">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-xl font-semibold text-gray-900">{t('demo_video_title')}</h3>
                <button onClick={handleCloseDemoVideo} className="text-gray-500 hover:text-gray-700 focus:outline-none">
                  ✖
                </button>
              </div>
              <div className="relative pt-0 flex justify-center items-center">
                <video className="w-[80%] max-w-full h-auto" controls autoPlay>
                  <source src="/demo" type="video/mp4" />
                  {t('video_error')}
                </video>
              </div>
              <div className="p-4 bg-gray-50 flex justify-between">
                <p className="text-sm text-gray-600">{t('demo_video_description')}</p>
                <button onClick={handleCloseDemoVideo} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">
                  {t('start_registration')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Titre principal */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">{t('become_client_title')}</h1>
              <p className="mt-2 text-lg text-gray-600">{t('become_client_description')}</p>
              {!playingDemo && (
                <button
                  onClick={() => setPlayingDemo(true)}
                  className="mt-4 inline-flex items-center text-gray-600 hover:text-gray-800"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l3-2z" clipRule="evenodd" />
                  </svg>
                  {t('watch_demonstration')}
                </button>
              )}
            </div>

            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex justify-between">
                {[t('step_information'), t('step_plan'), t('step_confirmation')].map((step, index) => (
                  <div
                    key={step}
                    className={`flex items-center ${
                      index < ['company-info', 'plan-selection', 'confirmation']
                        .indexOf(currentStep) + 1
                        ? 'text-gray-600'
                        : 'text-gray-400'
                    }`}
                  >
                    <span className="text-sm font-medium">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <div className="bg-white shadow rounded-lg p-6">
              {currentStep === 'company-info' && (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('company_information')}</h2>
                  <CompanyRegistrationForm onSubmit={handleCompanySubmit} />
                </>
              )}

              {currentStep === 'plan-selection' && (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('choose_plan')}</h2>
                  <PlanSelection
                    selectedPlan={selectedPlan}
                    onPlanSelect={handlePlanSelect}
                  />
                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedPlan}
                      className="px-6 py-2 bg-red-800 text-white rounded-md hover:bg-red-900 disabled:opacity-50 transition-colors"
                    >
                      {t('confirm')}
                    </button>
                  </div>
                </>
              )}

              {currentStep === 'confirmation' && (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('request_sent_success')}</h2>
                  <p className="text-gray-600 mb-6">{t('request_review_message')}</p>
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-800 transition-colors"
                  >
                    {t('back_to_homepage')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeClient;

// import { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CompanyRegistrationForm } from '../../Components/forms/CompanyRegistrationForm';
// import { PlanSelection } from '../../Components/forms/PlanSelection';
// import { companyApi } from '../../services/api';
// import Navbar from '../../Components/Navbar';

// type RegistrationStep = 'company-info' | 'plan-selection' | 'confirmation';

// export const BecomeClient = () => {
//   const navigate = useNavigate();
//   const [currentStep, setCurrentStep] = useState<RegistrationStep>('company-info');
//   const [registrationData, setRegistrationData] = useState<any>({});
//   const [selectedPlan, setSelectedPlan] = useState<string>('');
//   const [showDemoModal, setShowDemoModal] = useState<boolean>(true);
//   const [playingDemo, setPlayingDemo] = useState<boolean>(false);

//   // Réinitialiser le modal quand le composant est monté
//   useEffect(() => {
//     setShowDemoModal(true);
//     setPlayingDemo(false);
//   }, []);

//   const handleCompanySubmit = async (data: any) => {
//     setRegistrationData({ ...registrationData, ...data });
//     setCurrentStep('plan-selection');
//   };

//   const handlePlanSelect = (planId: string) => {
//     setSelectedPlan(planId);
//   };

//   const handleSubmit = async () => {
//     try {
//       const finalData = {
//         ...registrationData,
//         plan: selectedPlan,
//       };
//       await companyApi.register(finalData);
//       setCurrentStep('confirmation');
//     } catch (error) {
//       console.error('Error registering company:', error);
//     }
//   };

//   const handleWatchDemo = () => {
//     setPlayingDemo(true);
//     setShowDemoModal(false);
//   };

//   const handleSkipDemo = () => {
//     setShowDemoModal(false);
//   };

//   const handleCloseDemoVideo = () => {
//     setPlayingDemo(false);
//   };

//   return (
//     <div>
//       <Navbar />
//       <div className="flex-1 bg-gray-50 min-h-screen">
//         {/* Modal de démonstration avec arrière-plan transparent */}
//         {showDemoModal && (
//           <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
//             <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
//               <h3 className="text-xl font-semibold text-gray-900 mb-4">Need help with registration?</h3>
//               <p className="text-gray-600 mb-6">
//                 Would you like to see a demo video explaining how to fill out your registration request?
//               </p>
//               <div className="flex justify-end space-x-4">
//                 <button
//                   onClick={handleSkipDemo}
//                   className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
//                 >
//                   No, thanks
//                 </button>
//                 <button
//                   onClick={handleWatchDemo}
//                   className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
//                 >
//                   Watch demo
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Vidéo de démonstration en mode ajustable */}
//         {playingDemo && (
//           <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
//             <div className="bg-white rounded-lg shadow-xl w-[60%] max-w-4xl relative">
//               <div className="flex justify-between items-center p-4 border-b">
//                 <h3 className="text-xl font-semibold text-gray-900">How to become a client</h3>
//                 <button onClick={handleCloseDemoVideo} className="text-gray-500 hover:text-gray-700 focus:outline-none">
//                   ✖
//                 </button>
//               </div>
//               <div className="relative pt-0 flex justify-center items-center">
//                 <video className="w-[80%] max-w-full h-auto" controls autoPlay>
//                   <source src="/demo" type="video/mp4" />
//                   Your Browser can't read this the video
//                 </video>
//               </div>
//               <div className="p-4 bg-gray-50 flex justify-between">
//                 <p className="text-sm text-gray-600">
//                   This video guides you through the client registration process.
//                 </p>
//                 <button onClick={handleCloseDemoVideo} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
//                   Start Registration
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}

//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//           <div className="max-w-3xl mx-auto">
//             {/* Titre principal */}
//             <div className="text-center mb-8">
//               <h1 className="text-3xl font-bold text-gray-900">Become a Client</h1>
//               <p className="mt-2 text-lg text-gray-600">
//                 Complete the following information to submit your request
//               </p>
//               {!playingDemo && (
//                 <button
//                   onClick={() => setPlayingDemo(true)}
//                   className="mt-4 inline-flex items-center text-indigo-600 hover:text-indigo-700"
//                 >
//                   <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
//                     <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l3-2z" clipRule="evenodd" />
//                   </svg>
//                   Watch demonstration
//                 </button>
//               )}
//             </div>

//             {/* Progress Steps */}
//             <div className="mb-8">
//               <div className="flex justify-between">
//                 {['Informations', 'Plan', 'Confirmation'].map((step, index) => (
//                   <div
//                     key={step}
//                     className={`flex items-center ${
//                       index < ['company-info', 'plan-selection', 'confirmation']
//                         .indexOf(currentStep) + 1
//                         ? 'text-indigo-600'
//                         : 'text-gray-400'
//                     }`}
//                   >
//                     <span className="text-sm font-medium">{step}</span>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Step Content */}
//             <div className="bg-white shadow rounded-lg p-6">
//               {currentStep === 'company-info' && (
//                 <>
//                   <h2 className="text-2xl font-bold text-gray-900 mb-6">
//                     Company Information
//                   </h2>
//                   <CompanyRegistrationForm onSubmit={handleCompanySubmit} />
//                 </>
//               )}

//               {currentStep === 'plan-selection' && (
//                 <>
//                   <h2 className="text-2xl font-bold text-gray-900 mb-6">
//                     Choose your plan
//                   </h2>
//                   <PlanSelection
//                     selectedPlan={selectedPlan}
//                     onPlanSelect={handlePlanSelect}
//                   />
//                   <div className="mt-8 flex justify-end">
//                     <button
//                       onClick={handleSubmit}
//                       disabled={!selectedPlan}
//                       className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
//                     >
//                       Confirm
//                     </button>
//                   </div>
//                 </>
//               )}

//               {currentStep === 'confirmation' && (
//                 <div className="text-center">
//                   <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
//                     <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
//                     </svg>
//                   </div>
//                   <h2 className="text-2xl font-bold text-gray-900 mb-4">
//                     Request sent successfully!
//                   </h2>
//                   <p className="text-gray-600 mb-6">
//                     Our team will review your request and contact you shortly.
//                   </p>
//                   <button
//                     onClick={() => navigate('/')}
//                     className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
//                   >
//                     Back to homepage
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default BecomeClient;

// import { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { CompanyRegistrationForm } from '../../Components/forms/CompanyRegistrationForm';
// import { PlanSelection } from '../../Components/forms/PlanSelection';
// import { companyApi } from '../../services/api';
// import Navbar from '../../Components/Navbar';

// type RegistrationStep = 'company-info' | 'plan-selection' | 'confirmation';

// export const BecomeClient = () => {
//   const navigate = useNavigate();
//   const [currentStep, setCurrentStep] = useState<RegistrationStep>('company-info');
//   const [registrationData, setRegistrationData] = useState<any>({});
//   const [selectedPlan, setSelectedPlan] = useState<string>('');

//   const handleCompanySubmit = async (data: any) => {
//     setRegistrationData({ ...registrationData, ...data });
//     setCurrentStep('plan-selection');
//   };

//   const handlePlanSelect = (planId: string) => {
//     setSelectedPlan(planId);
//   };

//   const handleSubmit = async () => {
//     try {
//       const finalData = {
//         ...registrationData,
//         plan: selectedPlan,
//       };
//       await companyApi.register(finalData);
//       setCurrentStep('confirmation');
//     } catch (error) {
//       console.error('Error registering company:', error);
//     }
//   };

//   return (
//     // <div className="flex flex-col min-h-screen">
//       <div >
//       <Navbar />
//       <div className="flex-1 bg-gray-50">
//         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//           <div className="max-w-3xl mx-auto">
//             {/* Progress Steps */}
//             <div className="mb-8">
//               <div className="flex justify-between">
//                 {['Informations', 'Plan', 'Confirmation'].map((step, index) => (
//                   <div
//                     key={step}
//                     className={`flex items-center ${
//                       index < ['company-info', 'plan-selection', 'confirmation']
//                         .indexOf(currentStep) + 1
//                         ? 'text-indigo-600'
//                         : 'text-gray-400'
//                     }`}
//                   >
//                     <span className="text-sm font-medium">{step}</span>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Step Content */}
//             <div className="bg-white shadow rounded-lg p-6">
//               {currentStep === 'company-info' && (
//                 <>
//                   <h2 className="text-2xl font-bold text-gray-900 mb-6">
//                     Informations de l'entreprise
//                   </h2>
//                   <CompanyRegistrationForm onSubmit={handleCompanySubmit} />
//                 </>
//               )}

//               {currentStep === 'plan-selection' && (
//                 <>
//                   <h2 className="text-2xl font-bold text-gray-900 mb-6">
//                     Choisissez votre plan
//                   </h2>
//                   <PlanSelection
//                     selectedPlan={selectedPlan}
//                     onPlanSelect={handlePlanSelect}
//                   />
//                   <div className="mt-8 flex justify-end">
//                     <button
//                       onClick={handleSubmit}
//                       disabled={!selectedPlan}
//                       className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
//                     >
//                       Confirmer
//                     </button>
//                   </div>
//                 </>
//               )}

//               {currentStep === 'confirmation' && (
//                 <div className="text-center">
//                   <h2 className="text-2xl font-bold text-gray-900 mb-4">
//                     Demande envoyée avec succès !
//                   </h2>
//                   <p className="text-gray-600 mb-6">
//                     Notre équipe va examiner votre demande et vous contactera rapidement.
//                   </p>
//                   <button
//                     onClick={() => navigate('/')}
//                     className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
//                   >
//                     Retour à l'accueil
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default BecomeClient;